import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Retry } from '@components/common';
import { metricSelector } from '@store/metricsChartContainer/selectors';
import { setMetrics } from '@store/metricsChartContainer/actions';
import ControlPanel from '../control-panel';
import styles from './index.less';
import Charts from './Charts';
import { TOOLS } from '../control-panel/Toolbar';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { getChartStyle } from '../control-panel/Resize';
import { useAtomValue } from 'jotai';
import { diagnosticsModeAtom, sizeAtom } from '../atoms';
import { tid } from '@utils/test';
import { useFetchDod } from '../hooks/useDodSeries';
import useFetchAIAlerts from '../hooks/alerts/useFetchAIAlerts';
import useFetchManualAlerts from '@components/metrics-panel-hz/hooks/alerts/useFetchManualAlerts';
import { useFetchMetricSeries } from '../hooks/useMetricSeries';
import useQueryMetrics from '../hooks/useQueryMetrics';
import { useFetchMetricFilterSeries } from '../hooks/useMetricFilterSeries';
import { metricPanelAtom } from '@src/atoms/panels';
import ErrorBoundary from '@components/common/Retry/ErrorBoundary';
import mobileDetect from '@utils/misc/mobileDetect';
import useLiveSummary from '../hooks/useLiveSummary';
import { useFetchCompare } from '../hooks/useCompareSeries';
import ExportCompareFilters from './ExportCompareFilters';
import { useRefetchOnMetricDeleteError } from '../hooks/useRefetchOnMetricDeleteError';
import { getFirstAppliedFilter } from '@src/store/common/selectors';
import { doesFilterContainsInvalidField } from '@src/hooks/useSetError';

const isMobile = mobileDetect();

const isEIModule = window.conviva.module === 'EI';

function MetricsPanel() {
  const dispatch = useDispatch();
  const metrics = useSelector(metricSelector);
  const [activeTool, setActiveTool] = useState(isMobile ? TOOLS.noTool : TOOLS.select);

  useQueryMetrics();

  const {
    isFetching: isMetricSeriesFetching,
    isError: isMetricSeriesError,
    refetch,
  } = useFetchMetricSeries({ enabled: true });
  const { isFetching: isMetricFilterFetching } = useFetchMetricFilterSeries({
    enabled: true,
  });
  useRefetchOnMetricDeleteError();
  const { isFetching: isDoDFetching } = useFetchDod({ enabled: true });
  const { isFetching: isAIAlertsFetching } = useFetchAIAlerts({
    enabled: true,
  });
  const { isFetching: isManualAlertsFetching } = useFetchManualAlerts({
    enabled: true,
  });

  const { isFetching: isCompareSeriesFetching } = useFetchCompare({
    enabled: true,
  });
  // TODO: is loading indicator required?
  const { isFetching: isLiveSummaryFetching } = useLiveSummary({
    enabled: true,
  });

  const size = useAtomValue(sizeAtom);
  const diagMode = useAtomValue(diagnosticsModeAtom);

  const sensors = useSensors(useSensor(PointerSensor));

  function handleDragEnd(event) {
    const { active, over } = event;
    const sourceIdx = metrics.indexOf(active.id);
    const destIdx = metrics.indexOf(over.id);

    // create new metric list
    const newMetricList = Array.from(metrics);
    const [reorderedItem] = newMetricList.splice(sourceIdx, 1);
    newMetricList.splice(destIdx, 0, reorderedItem);

    dispatch(setMetrics(newMetricList));
  }
  const appliedFilter = useSelector(getFirstAppliedFilter);
  const filterError = doesFilterContainsInvalidField(appliedFilter);
  // TODO: empty check
  // isEmpty={charts.length === 0}
  return (
    <div className={styles.metricsContainer} {...tid('metrics-panel')}>
      <ControlPanel
        activeTool={activeTool}
        setActiveTool={setActiveTool}
        panelLoading={
          isMetricFilterFetching ||
          isDoDFetching ||
          isAIAlertsFetching ||
          isManualAlertsFetching ||
          isCompareSeriesFetching
        } // toggles/secondary-series loading indicator
      />
      <div className={styles.loadingLayer}>
        <Retry
          isLoading={isMetricSeriesFetching}
          showError={isMetricSeriesError}
          fetchData={refetch}
          progress={true}
          showHeavyMetricMessage={isEIModule}
          queryError={filterError}
        >
          <div
            key="metric-charts"
            className={styles.metricsContainerBody}
            style={getChartStyle(size, diagMode)}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <Charts activeTool={activeTool} />
            </DndContext>
            <ExportCompareFilters />
          </div>
        </Retry>
      </div>
    </div>
  );
}

const MetricsPanelContainer = () => {
  const showPanel = useAtomValue(metricPanelAtom);
  return showPanel ? (
    <ErrorBoundary>
      <MetricsPanel />
    </ErrorBoundary>
  ) : null;
};

export default MetricsPanelContainer;
